import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, MessageSquare, BarChart3, Pencil, Trash2, Send, Play, Square, ShieldCheck, GripVertical } from 'lucide-react';
import { atlasApi, AtlasUser, Pesquisa, PesquisaTipo, PerguntaTipo, PerguntaPesquisa, NovaPesquisa } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';
import Responder from './Responder';
import Resultado from './Resultado';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

type View = 'lista' | 'responder' | 'resultado';

const TIPO_LABEL: Record<PesquisaTipo, string> = { enps: 'eNPS', clima: 'Clima', pulse: 'Pulse' };
const PERGUNTA_LABEL: Record<PerguntaTipo, string> = { nps: 'NPS (0–10)', escala_5: 'Escala 1–5', sim_nao: 'Sim / Não', texto: 'Texto livre' };

const StatusBadge: React.FC<{ status: Pesquisa['status'] }> = ({ status }) => {
  const map = {
    rascunho: 'bg-gray-100 text-gray-500',
    ativa: 'bg-emerald-100 text-emerald-700',
    encerrada: 'bg-rose-100 text-rose-700',
  } as const;
  const label = { rascunho: 'Rascunho', ativa: 'Ativa', encerrada: 'Encerrada' }[status];
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status]}`}>{label}</span>;
};

let _pid = 0;
const novaPergunta = (tipo: PerguntaTipo = 'escala_5'): PerguntaPesquisa => ({ id: `tmp_${++_pid}`, texto: '', tipo });

const PesquisasApp: React.FC<Props> = ({ user, podeEditar }) => {
  const [view, setView] = useState<View>('lista');
  const [itens, setItens] = useState<Pesquisa[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [fStatus, setFStatus] = useState('');
  const [selecionada, setSelecionada] = useState<string | null>(null);

  // modal criar/editar
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [tipo, setTipo] = useState<PesquisaTipo>('clima');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [anonima, setAnonima] = useState(true);
  const [perguntas, setPerguntas] = useState<PerguntaPesquisa[]>([novaPergunta('nps')]);
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.listarPesquisas(user, { status: fStatus || undefined })
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, fStatus]);

  useEffect(() => { if (view === 'lista') carregar(); }, [view, carregar]);

  const abrirNova = () => {
    setEditId(null); setTipo('clima'); setNome(''); setDescricao(''); setAnonima(true);
    setPerguntas([novaPergunta('nps')]); setFb(null); setModal(true);
  };

  const abrirEdicao = async (p: Pesquisa) => {
    setFb(null);
    try {
      const { pesquisa } = await atlasApi.getPesquisa(user, p._id);
      setEditId(pesquisa._id);
      setTipo(pesquisa.tipo);
      setNome(pesquisa.nome);
      setDescricao(pesquisa.descricao || '');
      setAnonima(pesquisa.anonima);
      setPerguntas((pesquisa.perguntas || []).map(q => ({ ...q })));
      setModal(true);
    } catch (e: any) {
      setErro(e.message || 'Erro ao abrir pesquisa');
    }
  };

  const addPergunta = () => setPerguntas(p => [...p, novaPergunta()]);
  const removerPergunta = (i: number) => setPerguntas(p => p.filter((_, idx) => idx !== i));
  const patchPergunta = (i: number, patch: Partial<PerguntaPesquisa>) =>
    setPerguntas(p => p.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const salvar = async () => {
    if (!nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    const limpas = perguntas.filter(q => q.texto.trim()).map(q => ({ id: q.id, texto: q.texto.trim(), tipo: q.tipo }));
    if (limpas.length === 0) { setFb({ tipo: 'erro', msg: 'Inclua ao menos uma pergunta com texto.' }); return; }
    const body: NovaPesquisa = { tipo, nome: nome.trim(), descricao: descricao.trim() || undefined, anonima, perguntas: limpas };
    setSalvando(true);
    setFb(null);
    try {
      if (editId) await atlasApi.editarPesquisa(user, editId, body);
      else await atlasApi.criarPesquisa(user, body);
      setModal(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const publicar = async (p: Pesquisa) => { try { await atlasApi.publicarPesquisa(user, p._id); carregar(); } catch (e: any) { setErro(e.message); } };
  const encerrar = async (p: Pesquisa) => { try { await atlasApi.encerrarPesquisa(user, p._id); carregar(); } catch (e: any) { setErro(e.message); } };
  const excluir = async (p: Pesquisa) => {
    if (!window.confirm(`Excluir a pesquisa "${p.nome}"?`)) return;
    try { await atlasApi.excluirPesquisa(user, p._id); carregar(); } catch (e: any) { setErro(e.message); }
  };

  const abrir = (v: View, id: string) => { setSelecionada(id); setView(v); };

  if (view === 'responder' && selecionada) {
    return <div className="p-6"><Responder user={user} pesquisaId={selecionada} onVoltar={() => { setView('lista'); setSelecionada(null); }} /></div>;
  }
  if (view === 'resultado' && selecionada) {
    return <div className="p-6"><Resultado user={user} pesquisaId={selecionada} onVoltar={() => { setView('lista'); setSelecionada(null); }} /></div>;
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><MessageSquare className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Pesquisas de Clima &amp; eNPS</h2>
          <p className="text-sm text-gray-500">Meça engajamento e clima organizacional.</p>
        </div>
        {podeEditar && (
          <button onClick={abrirNova} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nova pesquisa
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
          <option value="">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="ativa">Ativa</option>
          <option value="encerrada">Encerrada</option>
        </select>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhuma pesquisa cadastrada.</div>
      ) : (
        <div className="space-y-3">
          {itens.map(p => (
            <div key={p._id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900">{p.nome}</h3>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">{TIPO_LABEL[p.tipo]}</span>
                    <StatusBadge status={p.status} />
                    {p.anonima && <span className="text-xs text-emerald-600 flex items-center gap-0.5"><ShieldCheck className="w-3.5 h-3.5" /> anônima</span>}
                  </div>
                  {p.descricao && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.descricao}</p>}
                </div>
              </div>

              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {p.status === 'ativa' && (
                  <button onClick={() => abrir('responder', p._id)} className="text-xs text-purple-600 hover:underline flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Responder</button>
                )}
                {podeEditar && (
                  <button onClick={() => abrir('resultado', p._id)} className="text-xs text-gray-600 hover:underline flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> Resultado</button>
                )}
                {podeEditar && p.status === 'rascunho' && (
                  <>
                    <button onClick={() => abrirEdicao(p)} className="text-xs text-gray-600 hover:underline flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button onClick={() => publicar(p)} className="text-xs text-emerald-600 hover:underline flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Publicar</button>
                  </>
                )}
                {podeEditar && p.status === 'ativa' && (
                  <button onClick={() => encerrar(p)} className="text-xs text-rose-600 hover:underline flex items-center gap-1"><Square className="w-3.5 h-3.5" /> Encerrar</button>
                )}
                {podeEditar && (
                  <button onClick={() => excluir(p)} className="text-xs text-rose-600 hover:underline flex items-center gap-1 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar pesquisa' : 'Nova pesquisa'}</h3>
              <button onClick={() => setModal(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select className={inputCls} value={tipo} onChange={e => setTipo(e.target.value as PesquisaTipo)}>
                    <option value="clima">Clima</option>
                    <option value="enps">eNPS</option>
                    <option value="pulse">Pulse</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Nome *</label>
                  <input className={inputCls} value={nome} onChange={e => setNome(e.target.value)} placeholder="Pesquisa de Clima 2026" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Descrição</label>
                <textarea className={`${inputCls} min-h-[60px] resize-y`} value={descricao} onChange={e => setDescricao(e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={anonima} onChange={e => setAnonima(e.target.checked)} className="accent-purple-600" />
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Pesquisa anônima (não registra identidade do respondente)
              </label>

              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls}>Perguntas</label>
                  <button onClick={addPergunta} className="text-xs text-purple-600 hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Adicionar</button>
                </div>
                <div className="space-y-2">
                  {perguntas.map((q, i) => (
                    <div key={String(q.id)} className="flex items-start gap-2 bg-gray-50 rounded-lg p-2 border border-gray-100">
                      <GripVertical className="w-4 h-4 text-gray-300 mt-2 flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <input className={inputCls} value={q.texto} onChange={e => patchPergunta(i, { texto: e.target.value })} placeholder={`Pergunta ${i + 1}`} />
                        <select className={`${inputCls} text-xs`} value={q.tipo} onChange={e => patchPergunta(i, { tipo: e.target.value as PerguntaTipo })}>
                          {(Object.keys(PERGUNTA_LABEL) as PerguntaTipo[]).map(t => <option key={t} value={t}>{PERGUNTA_LABEL[t]}</option>)}
                        </select>
                      </div>
                      <button onClick={() => removerPergunta(i)} className="p-1.5 rounded text-rose-500 hover:bg-rose-50 mt-1" title="Remover"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PesquisasApp;
